import {Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {AsyncSubject, defer, merge, Observable, of, Subject} from 'rxjs';
import {share, takeUntil} from 'rxjs/operators';
import {incrementalMerge} from '../../common/rxjs/incremental-merge';
import {Dimension, Point} from './abstract-units';
import {Cell} from './cell';
import {CellActionType} from './layout-actions/cell-action-type';
import {LayoutChanges} from './layout-changes';
import {Widget} from './widget/widget';
import {WidgetNotification} from './widget/widget-notification';

export class LayoutBase {

    public size: Point;
    public tabId: string;
    public cells: Cell[];
    public widgets: Widget[];

    public displaySize: Point;

    public update$: Observable<LayoutChanges>;
    public widgetNotification$: Observable<WidgetNotification>;

    public destroy$: Observable<any>;

    protected destroySubject$ = new AsyncSubject<any>();
    protected updateSubject$ = new Subject<LayoutChanges>();
    protected widgets$ = new Subject<Widget[]>();

    constructor(
        size: Point,
        tabId: string,
        cells: Cell[],
        widgets: Widget[],
        public injector: Injector
    ) {
        this.destroy$ = this.destroySubject$.asObservable();
        this.update$ = this.updateSubject$.asObservable().pipe(
            takeUntil(this.destroy$)
        );
        this.widgetNotification$ = merge(
            this.widgets$,
            defer(() => of(this.widgets)) // Deferred analog of startWith operator.
        ).pipe(
            incrementalMerge(widget => widget.notification$),
            takeUntil(this.destroy$),
            share()
        );

        this.size = size.slice() as Point;
        this.tabId = tabId;
        this.cells = cells.slice();
        this.widgets = widgets.slice();
    }

    public destroy(): void {
        if (this.widgets && this.widgets.length) {
            this.widgets.forEach(widget => widget.destroy());
        }

        this.destroySubject$.next(true);
        this.destroySubject$.complete();
    }

    public commitChanges(changes: LayoutChanges): void {
        const removedCells: Cell[] = changes.getChangesByActionType(CellActionType.CELL_DELETE).cells;
        const removedWidgets: Widget[] = changes.getChangesByActionType(CellActionType.WIDGET_DELETE).widgets;
        const widgetsToDestroy: Widget[] = removedWidgets
            .filter(widget => !widget.cell || removedCells.includes(widget.cell));
        const newWidgets: Widget[] = _.uniq(changes.getChangesByActionType(CellActionType.WIDGET_ADD).widgets)
            .filter(widget => !removedWidgets.includes(widget) && !this.widgets.includes(widget));

        // Remove cells from layout.
        removedCells.forEach(cell => {
            widgetsToDestroy.push(...cell.widgets);

            _.remove(this.cells, layoutCell => layoutCell === cell);
        });

        // Remove widgets from layout.
        _.union(widgetsToDestroy)
            .forEach(widget => {
                _.remove(this.widgets, layoutWidget => layoutWidget === widget);
                widget.destroy();
            });

        // Add widgets to layout.
        this.widgets.push(...newWidgets);

        changes.commitResult = {
            removedCells,
            destroyedWidgets: widgetsToDestroy,
            newWidgets
        };

        this.widgets$.next(this.widgets);
        this.updateSubject$.next(changes);
    }

    public produceDisplayInfo(displaySize: Point): void {
        this.displaySize = displaySize.slice() as Point;

        this.updateCellsDisplayInfo();
    }

    public setLayoutSize(newLayoutSize: Point): void {
        const oldDisplaySize = this.displaySize;
        const isDisplaySizeChanged = !_.isEqual(oldDisplaySize, newLayoutSize);

        if (isDisplaySizeChanged) {
            this.produceDisplayInfo(newLayoutSize);
        }

        this.size = newLayoutSize.slice() as Point;
    }

    public updateCellsDisplayInfo(cells?: Cell[]): void {
        // To be overridden.
    }

    public calcDisplayDimension(value: number, axis: Dimension): number {
        const layoutSize = this.size[axis];
        const displayLayoutSize = this.displaySize[axis];

        return layoutSize === displayLayoutSize
            ? value
            : Math.round(displayLayoutSize * value / layoutSize);
    }

    public getOtherCells(exclusionCells: Cell | Cell[]): Cell[] {
        exclusionCells = (_.isArray(exclusionCells) ? exclusionCells : [exclusionCells]) as Cell[];

        return _.difference(this.cells, exclusionCells);
    }

    public registerCell(cell: Cell): void {
        const layoutCells: Cell[] = this.cells;
        const cellLayoutIndex: number = _.findIndex(layoutCells, currentCell => currentCell.id === cell.id);

        if (cellLayoutIndex >= 0) {
            layoutCells.splice(cellLayoutIndex, 1, cell);
        } else {
            layoutCells.push(cell);
        }

        cell.widgets.forEach(widget => this.registerWidget(widget));
    }

    public registerWidget(widget: Widget): void {
        const layoutWidgets = this.widgets;
        const widgetLayoutIndex: number = layoutWidgets.indexOf(widget);

        if (widgetLayoutIndex < 0) {
            layoutWidgets.push(widget);
        }
    }

    public setCellActiveWidget(cell: Cell, widget: Widget): LayoutChanges {
        const layoutChanges = new LayoutChanges();

        if (cell.activeWidget !== widget) {
            cell.activeWidget = widget;

            layoutChanges.add(
                'Cell active widget changed',
                {cell, widget},
                {actionType: CellActionType.CELL_CHANGE}
            );
        }

        return layoutChanges;
    }

}
